import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { caseVisitsResponseSchema, type Visit } from "../types";
import { getVisitQueryKey } from "./useVisit";

const CASE_VISITS_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/cases/:caseId/visits`;

export function getCaseVisitsQueryKey(params: { workplaceId: string; caseId: string }) {
  return ["home-health", params.workplaceId, "cases", params.caseId, "visits"];
}

export function useCaseVisits(
  caseId: string,
  options: UseQueryOptions<Visit[], unknown, Visit[]> = {}
) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";
  const queryKey = getCaseVisitsQueryKey({ workplaceId, caseId });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await get({
        url: CASE_VISITS_URL.replace(":workplaceId", workplaceId).replace(":caseId", caseId),
        responseSchema: caseVisitsResponseSchema,
      });

      return response.data.data;
    },
    retry: 0,
    staleTime: 1000 * 60 * 5,
    onSuccess: (visits) => {
      visits.forEach((visit) => {
        queryClient.setQueryData(getVisitQueryKey({ workplaceId, visitId: visit.id }), visit);
      });
    },
    refetchInterval: 1000 * 60 * 10,
    ...options,
  });
}
